'use client'

import CopyLinkIcon from 'public/assets/copy.svg'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import styles from './LinkableHeading.module.scss'

type LinkableHeadingProps = {
  level: 1 | 2 | 3 | 4 | 5 | 6
  id: string
}

const LinkableHeading = ({ id, level, children }: PropsWithChildren<LinkableHeadingProps>) => {
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [clicked, setClicked] = useState(false)

  async function headingLinkClicked() {
    const headingUrl = window.location.origin + window.location.pathname + '#' + id
    await navigator.clipboard.writeText(headingUrl).then(() => {
      setClicked(true)
      setTimeout(() => setClicked(false), 2000)
    })
  }

  useEffect(() => {
    function handleScroll() {
      if (headingRef.current) {
        const rect = headingRef.current.getBoundingClientRect()
        if (rect.top > 0 && rect.top < 150) {
          const event = new CustomEvent('active-heading-changed', { detail: { headingId: id } })
          window.dispatchEvent(event)
        }
      }
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [id])

  switch (level) {
    case 1:
      return (
        <h1 id={id} className={styles.heading + (clicked ? ' ' + styles.clicked : '')} onClick={() => headingLinkClicked()}>
          {children}
          <div className={styles.tooltip}>
            <CopyLinkIcon className={styles['copy-link']} />
            <span className={styles['tooltip-text']}>Copied!</span>
          </div>
        </h1>
      )
    case 2:
      return (
        <h2 ref={headingRef} id={id} className={styles.heading + (clicked ? ' ' + styles.clicked : '')} onClick={() => headingLinkClicked()}>
          {children}
          <div className={styles.tooltip}>
            <CopyLinkIcon className={styles['copy-link']} />
            <span className={styles['tooltip-text']}>Copied!</span>
          </div>
        </h2>
      )
    case 3:
      return (
        <h3 ref={headingRef} id={id} className={styles.heading + (clicked ? ' ' + styles.clicked : '')} onClick={() => headingLinkClicked()}>
          {children}
          <div className={styles.tooltip}>
            <CopyLinkIcon className={styles['copy-link'] + ' ' + styles.smaller} />
            <span className={styles['tooltip-text'] + ' ' + styles.smaller}>Copied!</span>
          </div>
        </h3>
      )
    case 4:
      return (
        <h4 id={id} className={styles.heading + (clicked ? ' ' + styles.clicked : '')} onClick={() => headingLinkClicked()}>
          {children}
          <div className={styles.tooltip}>
            <CopyLinkIcon className={styles['copy-link']} />
            <span className={styles['tooltip-text']}>Copied!</span>
          </div>
        </h4>
      )
    case 5:
      return (
        <h5 id={id} className={styles.heading + (clicked ? ' ' + styles.clicked : '')} onClick={() => headingLinkClicked()}>
          {children}
          <div className={styles.tooltip}>
            <CopyLinkIcon className={styles['copy-link']} />
            <span className={styles['tooltip-text']}>Copied!</span>
          </div>
        </h5>
      )
    case 6:
      return (
        <h6 id={id} className={styles.heading + (clicked ? ' ' + styles.clicked : '')} onClick={() => headingLinkClicked()}>
          {children}
          <div className={styles.tooltip}>
            <CopyLinkIcon className={styles['copy-link']} />
            <span className={styles['tooltip-text']}>Copied!</span>
          </div>
        </h6>
      )
  }
}

export default LinkableHeading
